import { Box, Divider, Input, Text } from '@wix/design-system';
import lodash from 'lodash';
import type { PropsWithChildren } from 'react';
import React, { useContext, useRef } from 'react';

import { ModalServiceContext, RicosContext } from './../../ricos-context';
import type { LogBiFn } from './../../_shared/bi-context-provider/bi-context-provider';
import type { PmNodeType } from './../../_shared/pm-node-types';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { ButtonColorPicker } from './../components/button-color-picker';
import { buttonsModals } from './../constants';
import { ButtonColorSwatches, SliderWithInput } from './../../ui-components';
import { ColorPickerLiveSiteColorHint } from './../../ui-components/color-picker/button-color-swatches/live-site-color-hint';

import {
  DESIGN_SETTINGS_BACKGROUND_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_BACKGROUND_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_BACKGROUND_COLOR_SWATCHES,
  DESIGN_SETTINGS_BORDER_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_BORDER_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_BORDER_COLOR_SWATCHES,
  DESIGN_SETTINGS_BORDER_WIDTH_INPUT,
  DESIGN_SETTINGS_BORDER_WIDTH_SLIDER,
  DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
  DESIGN_SETTINGS_CORNER_RADIUS_SLIDER,
  DESIGN_SETTINGS_TEXT_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_TEXT_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_TEXT_COLOR_SWATCHES,
} from '../data-hooks';
import type { ButtonPluginEditorConfigCommon } from '../types';

type DesignComponentProps = {
  stylesData: StylesData;
  config: ButtonPluginEditorConfigCommon;
  onChange: (data: StylesData) => void;
  defaultBackgroundColor?: string;
  defaultTextColor?: string;
  nodeId: string;
  nodeType: typeof PmNodeType.LINK_BUTTON | typeof PmNodeType.ACTION_BUTTON;
  logBi: LogBiFn;
};

export type StylesData = {
  border: {
    width: number;
    radius: number;
  };
  colors: {
    text: string | undefined;
    border: string | undefined;
    background: string | undefined;
  };
};

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export type ButtonColorType = keyof StylesData['colors'];

export const DesignComponent: React.FC<DesignComponentProps> = ({
  stylesData,
  config,
  onChange,
  defaultBackgroundColor,
  defaultTextColor,
  nodeId,
  nodeType,
  logBi,
}) => {
  const { t, isMobile, environment } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);
  const { pmNodeAttrs } = useNodeAttrs(nodeId, nodeType);

  const backgroundMoreButtonRef = useRef<HTMLDivElement>(null);
  const textMoreButtonRef = useRef<HTMLDivElement>(null);
  const borderMoreButtonRef = useRef<HTMLDivElement>(null);

  const handleOnChange = (data: RecursivePartial<StylesData>) => {
    onChange({
      border: { ...stylesData.border, ...data.border },
      colors: { ...stylesData.colors, ...data.colors },
    });
  };

  const showLiveSiteHint = environment !== 'liveSite';

  const openColorPicker = (colorType: ButtonColorType) => {
    const referenceElement = getButtonRef(colorType);
    const modalId = buttonsModals.colorPicker;

    modalService.togglePluginPanel({
      id: modalId,
      Component: ButtonColorPicker,
      componentProps: {
        modalId,
        nodeId,
        nodeType,
        colorType,
        referenceElement,
        config,
        defaultTextColor,
        defaultBackgroundColor,
        onColorSelected: onColorSelected(colorType),
        onColorAdded: onColorAdded(colorType),
      },
      isMobile,
      positioning: {
        referenceElement,
        placement: 'left',
      },
      type: undefined,
      onClickOutside: lodash.noop,
      focusReturnElement: referenceElement ?? undefined,
    });
  };

  const onColorSelected = (colorType: ButtonColorType) => (color: string | undefined) =>
    handleOnChange({ colors: { [colorType]: color } });

  const currentColor = (colorType: ButtonColorType) => pmNodeAttrs?.styles?.colors?.[colorType];

  const onColorChange = (colorType: ButtonColorType) => (color: string | undefined) =>
    handleOnChange({ colors: { [colorType]: color } });

  const onColorAdded = (colorType: ButtonColorType) => (color: string) => {
    if (colorType === 'background') {
      return config.onBackgroundColorAdded?.(color);
    } else if (colorType === 'text') {
      return config.onTextColorAdded?.(color);
    } else {
      return config.onBorderColorAdded?.(color);
    }
  };

  const getButtonRef = (colorType: ButtonColorType) => {
    if (colorType === 'background') {
      return backgroundMoreButtonRef.current;
    } else if (colorType === 'text') {
      return textMoreButtonRef.current;
    } else {
      return borderMoreButtonRef.current;
    }
  };

  return (
    <Box direction="vertical" width={isMobile ? '100%' : undefined} overflowX="auto">
      <Section>
        <Box direction="vertical" gap="SP3" paddingTop="SP2">
          <ButtonColorSwatches
            label={t('ButtonModal_Background_Color')}
            color={currentColor('background')}
            onChange={color => {
              onColorChange('background')(color);
              logBi({ dataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_PREDEFINED_SWATCH, value: color });
            }}
            dataHook={DESIGN_SETTINGS_BACKGROUND_COLOR_SWATCHES}
            onMorePress={() => {
              openColorPicker('background');
              logBi({ dataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_MORE_BUTTON });
            }}
            defaultColor={defaultBackgroundColor}
            moreButtonRef={backgroundMoreButtonRef}
          />
          {showLiveSiteHint && <ColorPickerLiveSiteColorHint />}
          <SliderWithInput
            min={0}
            max={30}
            onChange={radius => handleOnChange({ border: { radius } })}
            label={t('ButtonModal_Radius_Input')}
            sliderDataHook={DESIGN_SETTINGS_CORNER_RADIUS_SLIDER}
            inputDataHook={DESIGN_SETTINGS_CORNER_RADIUS_INPUT}
            value={stylesData.border.radius}
            suffix={<Input.Affix>{t('Common_Units_PX')}</Input.Affix>}
            logBi={logBi}
          />
        </Box>
      </Section>
      <Divider />
      <Section title={t('ButtonModal_Text_Section')}>
        <ButtonColorSwatches
          label={t('ButtonModal_Text_Color')}
          color={currentColor('text')}
          onChange={color => {
            onColorChange('text')(color);
            logBi({ dataHook: DESIGN_SETTINGS_TEXT_COLOR_PREDEFINED_SWATCH, value: color });
          }}
          dataHook={DESIGN_SETTINGS_TEXT_COLOR_SWATCHES}
          defaultColor={defaultTextColor}
          onMorePress={() => {
            openColorPicker('text');
            logBi({ dataHook: DESIGN_SETTINGS_TEXT_COLOR_MORE_BUTTON });
          }}
          moreButtonRef={textMoreButtonRef}
        />
      </Section>
      <Divider />
      <Section title={t('ButtonModal_Border_Section')}>
        <Box direction="vertical" gap="SP3" paddingTop="SP2">
          <ButtonColorSwatches
            label={t('ButtonModal_Border_Color')}
            color={currentColor('border')}
            onChange={color => {
              onColorChange('border')(color);
              logBi({ dataHook: DESIGN_SETTINGS_BORDER_COLOR_PREDEFINED_SWATCH, value: color });
            }}
            dataHook={DESIGN_SETTINGS_BORDER_COLOR_SWATCHES}
            onMorePress={() => {
              openColorPicker('border');
              logBi({ dataHook: DESIGN_SETTINGS_BORDER_COLOR_MORE_BUTTON });
            }}
            defaultColor={undefined}
            moreButtonRef={borderMoreButtonRef}
          />
          <SliderWithInput
            min={0}
            max={15}
            onChange={width => handleOnChange({ border: { width } })}
            label={t('ButtonModal_Width_Input')}
            sliderDataHook={DESIGN_SETTINGS_BORDER_WIDTH_SLIDER}
            inputDataHook={DESIGN_SETTINGS_BORDER_WIDTH_INPUT}
            value={stylesData.border.width}
            suffix={<Input.Affix>{t('Common_Units_PX')}</Input.Affix>}
            logBi={logBi}
          />
        </Box>
      </Section>
    </Box>
  );
};

const Section: React.FC<PropsWithChildren<{ title?: string }>> = ({ title, children }) => {
  return (
    <Box direction="vertical">
      {title && (
        <Box padding="SP3" paddingBottom="SP2">
          <Text size="small" weight="bold">
            {title}
          </Text>
        </Box>
      )}
      <Box direction="vertical" padding="SP3" paddingTop="SP1" gap="SP3">
        {children}
      </Box>
    </Box>
  );
};
